/**
 * Registros fechados no navegador (matriz de funcionalidades, F7; decisões 277 e 293).
 *
 * No teste, o que passa do limite de leitura da lista é fechado com a chave pública do servidor
 * (WebCrypto: AES-GCM de 256 bits por registro, com a chave embrulhada em RSA-OAEP/SHA-256) e
 * gravado no navegador só assim: o fechado (`sealed`) e uma versão mínima e embaralhada para a
 * tela, com `deface: true`. Só o servidor abre (`lib/entitlements/seal.js` do backend), na cópia da
 * lista; depois da compra, a lista é lida de novo do servidor.
 *
 * A chave do registro e o `cid` viram o mesmo código opaco que o servidor usa ao mascarar
 * (`masked:` + 20 hexadecimais do SHA-1), para nada duplicar entre computadores.
 */
import { defaceString } from './deface';

/** O fechado, no formato que o servidor abre. */
export interface SealedRecord {
  v: 1;
  /** A chave AES embrulhada com a chave pública (base64). */
  k: string;
  /** 12 bytes (base64). */
  iv: string;
  /** O JSON do registro original cifrado, com a etiqueta no fim (base64). */
  c: string;
}

const keyCache = new Map<string, Promise<CryptoKey>>();

function bytesToBase64(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = '';
  for (let i = 0; i < view.length; i += 1) binary += String.fromCharCode(view[i] ?? 0);
  return btoa(binary);
}

function base64ToBytes(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value);
  const out = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

/** A chave pública do servidor (SPKI em base64, do `/me`), importada uma vez. */
export function importSealKey(spkiBase64: string): Promise<CryptoKey> {
  let key = keyCache.get(spkiBase64);
  if (!key) {
    key = crypto.subtle.importKey(
      'spki',
      base64ToBytes(spkiBase64),
      { name: 'RSA-OAEP', hash: 'SHA-256' },
      false,
      ['wrapKey'],
    );
    // Chave que não importa não fica guardada: o próximo `/me` pode trazer outra.
    key.catch(() => keyCache.delete(spkiBase64));
    keyCache.set(spkiBase64, key);
  }
  return key;
}

/** O mesmo código opaco do servidor: `masked:` + os 20 primeiros hexadecimais do SHA-1. */
export async function maskedKey(key: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(key));
  const hex = Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
  return `masked:${hex.slice(0, 20)}`;
}

/** Fecha o registro inteiro com a chave pública. */
export async function sealRecord(record: object, publicKey: CryptoKey): Promise<SealedRecord> {
  const aes = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt']);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    aes,
    new TextEncoder().encode(JSON.stringify(record)),
  );
  const wrapped = await crypto.subtle.wrapKey('raw', aes, publicKey, { name: 'RSA-OAEP' });
  return { v: 1, k: bytesToBase64(wrapped), iv: bytesToBase64(iv), c: bytesToBase64(data) };
}

/** Campos que a tela usa para desenhar a linha embaralhada; o resto fica só no fechado. */
const VISIBLE_TEXT = ['value', 'title', 'company', 'headline', 'location', 'category'] as const;

/**
 * O que o navegador guarda no lugar do registro: o fechado, a chave opaca e só os textos que a tela
 * mostra, embaralhados. Sem coordenadas, links de perfil, imagens ou o `cid` verdadeiro.
 */
export async function sealAndMask<T extends { key: string; listId: string }>(
  original: T,
  publicKey: CryptoKey,
): Promise<T> {
  const record = original as unknown as Record<string, unknown>;
  const sealed = await sealRecord(original, publicKey);
  const key = await maskedKey(original.key);
  const out: Record<string, unknown> = {
    listId: original.listId,
    key,
    createdAt: record['createdAt'],
    deface: true,
    sealed,
  };
  if ('cid' in record) out['cid'] = key;
  if (typeof record['type'] === 'string') out['type'] = record['type'];
  if (typeof record['mobile'] === 'boolean') out['mobile'] = record['mobile'];
  for (const field of VISIBLE_TEXT) {
    const value = record[field];
    if (typeof value === 'string') out[field] = defaceString(value);
  }
  for (const field of ['address', 'categories', 'websites'] as const) {
    const value = record[field];
    if (Array.isArray(value)) {
      out[field] = value
        .filter((line) => typeof line === 'string')
        .map((line) => defaceString(line));
    }
  }
  if (Array.isArray(record['phones'])) {
    // Os números do lugar, embaralhados, só para a linha ter o mesmo desenho.
    out['phones'] = (record['phones'] as Array<Record<string, unknown>>).map((phone) => ({
      ...(typeof phone['number'] === 'string' ? { number: defaceString(phone['number']) } : {}),
      ...(typeof phone['formated'] === 'string'
        ? { formated: defaceString(phone['formated']) }
        : {}),
      ...(typeof phone['mobile'] === 'boolean' ? { mobile: phone['mobile'] } : {}),
    }));
  }
  return out as unknown as T;
}
